import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { SECONDARY_BUTTON_SX } from '../components/shared/dialogActionsSx';
import { ToolbarPageActions } from '../components/layout/TopToolbarContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { isPsiEmail } from '../utils/psiStaff';
// CircularProgress removed - skeleton cards used instead
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../stores/globalStore';
import {
  useDashboardLayout,
  useDashboardData,
  useSaveDashboardLayout,
  useResetDashboardLayout,
  useAllMealPatternNotes,
  useDashboardCensus,
  useDashboardBirthdays,
  useDashboardWeightAlerts,
  useDashboardMealServiceStatus,
} from '../api/dashboardApi';
import { usePreference, PREF_KEYS } from '../hooks/useUserPreferences';
import { useSecurityVisibility } from '../hooks/useSecurityVisibility';
import { useFacilityHeldDietOrders, dietOrderLockKeys } from '../api/dietOrderLockApi';
import {
  DEFAULT_DASHBOARD_CARDS,
  type DashboardCardConfig,
} from '../types/dashboard';
import DietOrderChangesCard from './dashboard/DietOrderChangesCard';
import TrayLineWarningsCard from './dashboard/TrayLineWarningsCard';
import AdmittedResidentsCard from './dashboard/AdmittedResidentsCard';
import AdmittedGuestsCard from './dashboard/AdmittedGuestsCard';
import DischargedResidentsCard from './dashboard/DischargedResidentsCard';
import UnitRoomBedChangesCard from './dashboard/UnitRoomBedChangesCard';
import AllergyChangesCard from './dashboard/AllergyChangesCard';
import MealPatternNotesCard from './dashboard/MealPatternNotesCard';
import SelectiveMenuAutoCard from './dashboard/SelectiveMenuAutoCard';
import CensusSummaryCard from './dashboard/CensusSummaryCard';
import WeightAlertsCard from './dashboard/WeightAlertsCard';
import UpcomingBirthdaysCard from './dashboard/UpcomingBirthdaysCard';
import BedsideDevicesCard from './dashboard/BedsideDevicesCard';
import QuickActions from './dashboard/QuickActions';
import NowStrip from './dashboard/NowStrip';
import AttentionRows from './dashboard/AttentionRows';
import HeldEhrChangesCard from './dashboard/HeldEhrChangesCard';
import SetupChecklistCard from './getting-started/SetupChecklistCard';
import { heldChangesApply, heldChangesUnavailableReason } from './dashboard/heldChangesAvailability';
import type { DashboardActionCommand } from './dashboard/dashboardActions';
import TrayTicketVisualizerDialog from './menu-cycle/TrayTicketVisualizerDialog';
import { SkeletonCard } from './dashboard/DashboardCard';
import './Dashboard.css';
import SelectField from '../components/form/SelectField';

/**
 * Card ids that no longer render, for any user.
 *
 * Saved layouts outlive the cards they name, so a retired id has to be
 * filtered rather than deleted from the defaults — otherwise an old row
 * resurrects a card that no longer exists.
 *
 *   QuickActions / TubeFeedSummary  removed before this rewrite
 *   MealServiceStatus               absorbed into the Now strip, which is
 *                                   always on screen and never collapses
 *
 * One list because there are two consumers — the grid and the edit-mode
 * checkbox panel — and as two literals they had already drifted apart.
 */
const RETIRED_CARD_IDS: ReadonlySet<string> = new Set([
  'QuickActions',
  'TubeFeedSummary',
  'MealServiceStatus',
]);

/** Time span choices for the dashboard's change cards, in hours. */
const TIME_SPAN_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 6, label: '6h' },
  { value: 12, label: '12h' },
  { value: 24, label: '24h' },
  { value: 48, label: '48h' },
  { value: 72, label: '72h' },
  { value: 168, label: '7 days' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const isCorporate = useGlobalStore((s) => s.facilitySettings?.isCorporate);
  const residentSingular = useGlobalStore((s) => s.residentLabel);
  const residentPlural = useGlobalStore((s) => s.residentPluralLabel);
  // Card titles are stored on the layout config (persisted to DB
  // via `useSaveDashboardLayout`) with the WinForms defaults —
  // "Admitted Residents", "Discharged Residents", etc. Facilities
  // that customize the resident label ("Patients", "Clients", …)
  // need those substitutions applied at render time so the
  // visibility checklist matches what the actual card renders.
  // Whole-word replacement in either order (plural first so
  // "Residents" doesn't get mangled by a naive singular pass).
  const localizeResident = useCallback((text: string) => {
    return text
      .replace(/\bResidents\b/g, residentPlural)
      .replace(/\bResident\b/g, residentSingular);
  }, [residentSingular, residentPlural]);
  const queryClient = useQueryClient();

  // Corporate facilities don't have resident data — redirect to Corporate Dashboard
  useEffect(() => {
    if (isCorporate) navigate('/corporate-dashboard', { replace: true });
  }, [isCorporate, navigate]);

  // Default 72h — spin edit steps at 6h between 6 and 168 (7 days).
  const [selectedHours, setSelectedHours] = usePreference(PREF_KEYS.DASHBOARD_HOURS, 72);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAllMPNotes, setShowAllMPNotes] = useState(false);
  const [lastUpdatedAt, setLastUpdated] = useState<Date | null>(null);
  const lastUpdated = lastUpdatedAt ? lastUpdatedAt.toLocaleString() : '';
  const lastUpdatedTime = lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

  // Layout state (local copy for editing)
  const [localCards, setLocalCards] = useState<DashboardCardConfig[] | null>(null);
  const originalCardsRef = useRef<DashboardCardConfig[] | null>(null);

  // Drag state
  const dragIndexRef = useRef<number | null>(null);

  // Queries
  const layoutQuery = useDashboardLayout(facilityId, userId);
  const dataQuery = useDashboardData(facilityId, selectedHours);
  const allMPNotesQuery = useAllMealPatternNotes(facilityId, showAllMPNotes);
  const saveMutation = useSaveDashboardLayout(facilityId);
  const resetMutation = useResetDashboardLayout(facilityId, userId);

  // Queries - new cards (independent, fetched in parallel by React Query)
  const censusQuery = useDashboardCensus(facilityId);
  const birthdaysQuery = useDashboardBirthdays(facilityId);
  const weightAlertsQuery = useDashboardWeightAlerts(facilityId);
  const mealStatusQuery = useDashboardMealServiceStatus(facilityId);

  // Held EHR diet changes: only where WinForms shows pending diets (EHR
  // active and DietOrderLocking_Active). Anywhere else the card is not
  // drawn at all (Jeff, 2026-10-22: a card saying a feature is off is
  // clutter). The rows load only for users who can see diet fluctuations;
  // others get the card with the reason.
  const ehrTitle = useGlobalStore((s) => s.ehrTitleLabel) || 'EHR';
  const facilitySettingsKnown = useGlobalStore((s) => s.facilitySettings != null);
  const facilityEhrActive = useGlobalStore((s) => !!s.facilitySettings?.facilityEhrActive);
  const dietOrderLockingActive = useGlobalStore((s) => !!s.facilitySettings?.dietOrderLocking_Active);
  const isPsi = isPsiEmail(useGlobalStore((s) => s.userEmail));
  const { canViewDietFluctuations } = useSecurityVisibility();
  const heldApplies = facilitySettingsKnown && heldChangesApply({ facilityEhrActive, dietOrderLockingActive });
  const heldUnavailableReason = heldChangesUnavailableReason({
    ehrTitle,
    facilityEhrActive,
    dietOrderLockingActive,
    canViewDietFluctuations,
  });
  const heldEnabled = heldApplies && heldUnavailableReason == null;
  const heldQuery = useFacilityHeldDietOrders(facilityId, heldEnabled);
  const [bedsideHealth, setBedsideHealth] = useState<import('../api/bedsideApi').BedsideDeviceHealthSummary>({ total: 0, active: 0, expiringSoon: 0, expired: 0, duplicateRoomBeds: [] });

  // Resolved cards: local edits take precedence, then server, then defaults
  const cards = localCards ?? layoutQuery.data?.cards ?? DEFAULT_DASHBOARD_CARDS;

  // Quick actions save immediately rather than waiting for Save Layout:
  // pinning a button is a one-click decision and having to remember a second
  // step to keep it would be its own bug. Held locally as well so the row
  // updates on the click rather than on the round-trip.
  const [localActions, setLocalActions] = useState<string[] | null>(null);
  const actionIds = localActions ?? layoutQuery.data?.actions ?? null;
  const [visualizerOpen, setVisualizerOpen] = useState(false);
  // Filter also excludes retired cardIds (`QuickActions`,
  // `TubeFeedSummary`) so orphan entries in server-saved layouts
  // don't produce empty resizable wrappers on the grid.
  const visibleCards = [...cards]
    .filter((c) => c.isVisible)
    .filter((c) => !RETIRED_CARD_IDS.has(c.cardId))
    .sort((a, b) => a.order - b.order);

  const bedsideVisible = cards.some(c => c.cardId === 'BedsideDevices' && c.isVisible);

  useEffect(() => {
    if (facilityId > 0 && bedsideVisible) {
      import('../api/bedsideApi').then(({ getBedsideHealth }) => {
        getBedsideHealth(facilityId).then(setBedsideHealth).catch(() => {});
      });
    }
  }, [facilityId, bedsideVisible]);

  // Track last updated time
  const isLoading = dataQuery.isLoading;
  if (dataQuery.data && !lastUpdatedAt) {
    setLastUpdated(new Date());
  }

  // --- Actions ---

  // Aggregate `isFetching` across every top-level dashboard query.
  // Individual card queries also key under `['dashboard', ...]` so
  // `invalidateQueries` refetches them too, but we watch only the
  // queries owned at this level for the spinner state — anything
  // deeper flips its own card-local skeleton on refetch.
  const isRefreshing =
    dataQuery.isFetching
    || censusQuery.isFetching
    || birthdaysQuery.isFetching
    || weightAlertsQuery.isFetching
    || mealStatusQuery.isFetching
    || allMPNotesQuery.isFetching
    || (heldEnabled && heldQuery.isFetching);

  const handleRefresh = useCallback(async () => {
    // `invalidateQueries` with the shared root key marks every
    // `['dashboard', …]` query stale AND triggers an immediate
    // refetch of the active (mounted) ones. Awaiting the returned
    // promise lets us keep the spinner spinning until the actual
    // network round-trips settle, so users see feedback even when
    // the payload is unchanged.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: dietOrderLockKeys.heldFacility(facilityId) }),
    ]);
    setLastUpdated(new Date());
  }, [queryClient, facilityId]);

  // A select commits one value per change, so the time span applies at
  // once; the debounce was for the old 6-hour stepper's rapid clicks.
  const handleHoursChange = (value: number) => {
    setSelectedHours(value);
    setLastUpdated(null);
  };

  // --- Edit Mode ---

  const enterEditMode = () => {
    originalCardsRef.current = cards.map((c) => ({ ...c }));
    setLocalCards(cards.map((c) => ({ ...c })));
    setIsEditMode(true);
  };

  const cancelEdit = () => {
    setLocalCards(originalCardsRef.current);
    originalCardsRef.current = null;
    setIsEditMode(false);
  };

  const saveLayout = async () => {
    if (!localCards) return;
    setIsEditMode(false);
    await saveMutation.mutateAsync({
      fKey_User: userId,
      cards: localCards,
      columnsCount: 3,
      // Carry the pinned actions through — the whole settings object is
      // serialized as one blob, so omitting them here would silently wipe
      // them every time someone saved a card layout.
      actions: actionIds ?? undefined,
      lastModified: new Date().toISOString(),
    });
    originalCardsRef.current = null;
  };

  const saveActions = async (ids: string[]) => {
    setLocalActions(ids);
    await saveMutation.mutateAsync({
      fKey_User: userId,
      cards,
      columnsCount: 3,
      actions: ids,
      lastModified: new Date().toISOString(),
    });
  };

  const runCommand = (command: DashboardActionCommand) => {
    switch (command) {
      case 'visualizer':
        setVisualizerOpen(true);
        break;
      case 'printTrayTickets':
        // The tray-ticket report owns its own options dialog, so the action
        // hands off to it rather than duplicating the parameter form here.
        navigate('/reports/run/tray-tickets');
        break;
    }
  };

  const resetLayout = async () => {
    setIsEditMode(false);
    setLocalCards(null);
    originalCardsRef.current = null;
    await resetMutation.mutateAsync();
  };

  const toggleCardVisibility = (cardId: string, visible: boolean) => {
    setLocalCards((prev) =>
      (prev ?? cards).map((c) =>
        c.cardId === cardId ? { ...c, isVisible: visible } : c
      )
    );
  };

  // Pixel height for a single card, updated live from the drag
  // handle in edit mode. Nullish clears back to auto (content
  // sizing). Persisted with the rest of the layout on Save Layout.
  const setCardHeight = useCallback((cardId: string, height: number | null) => {
    setLocalCards((prev) =>
      (prev ?? cards).map((c) =>
        c.cardId === cardId ? { ...c, height: height ?? undefined } : c
      )
    );
  }, [cards]);

  // --- Drag and Drop ---

  const handleDragStart = (idx: number) => (e: React.DragEvent) => {
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    (e.currentTarget as HTMLElement).classList.add('dragging');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  /**
   * Swap two cards by their position among the visible, ordered cards.
   *
   * Pulled out of handleDrop so the keyboard path below reorders through
   * exactly the same code. Reordering used to be drag-only, which made
   * arranging the dashboard impossible without a mouse.
   */
  const swapCards = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;

    setLocalCards((prev) => {
      const list = [...(prev ?? cards)];
      const visible = list.filter((c) => c.isVisible).sort((a, b) => a.order - b.order);

      const dragCardId = visible[fromIdx]?.cardId;
      const dropCardId = visible[toIdx]?.cardId;
      if (!dragCardId || !dropCardId) return list;

      // Swap orders
      const dragOrder = list.find((c) => c.cardId === dragCardId)!.order;
      const dropOrder = list.find((c) => c.cardId === dropCardId)!.order;

      return list.map((c) => {
        if (c.cardId === dragCardId) return { ...c, order: dropOrder };
        if (c.cardId === dropCardId) return { ...c, order: dragOrder };
        return c;
      });
    });
  };

  const handleDrop = (dropIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const dragIdx = dragIndexRef.current;
    if (dragIdx == null) return;
    swapCards(dragIdx, dropIdx);
  };

  /**
   * Keyboard equivalent of dragging a card. Ctrl+Arrow rather than a bare
   * arrow so the keys still scroll the page normally while a card has focus.
   */
  const handleCardMove = (idx: number) => (direction: -1 | 1) => {
    const target = idx + direction;
    if (target < 0 || target >= visibleCards.length) return;
    swapCards(idx, target);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    document.querySelectorAll('.dashboard-card.dragging').forEach((el) => {
      el.classList.remove('dragging');
    });
  };

  // --- Render Card by ID ---

  const data = dataQuery.data;

  const renderCard = (config: DashboardCardConfig, idx: number) => {
    const dragProps = isEditMode
      ? {
          draggable: true,
          onDragStart: handleDragStart(idx),
          onDragOver: handleDragOver,
          onDrop: handleDrop(idx),
          onDragEnd: handleDragEnd,
          onMove: handleCardMove(idx),
          movePosition: { index: idx, total: visibleCards.length },
        }
      : {};

    switch (config.cardId) {
      case 'DietOrderChanges':
        return <DietOrderChangesCard key={config.cardId} data={data?.dietOrderChanges ?? []} {...dragProps} />;
      case 'TrayLineWarnings':
        return <TrayLineWarningsCard key={config.cardId} data={data?.trayLineWarnings ?? []} {...dragProps} />;
      case 'AdmittedResidents':
        return <AdmittedResidentsCard key={config.cardId} data={data?.admittedResidents ?? []} {...dragProps} />;
      case 'AdmittedGuests':
        return <AdmittedGuestsCard key={config.cardId} data={data?.admittedGuests ?? []} {...dragProps} />;
      case 'DischargedResidents':
        return <DischargedResidentsCard key={config.cardId} data={data?.dischargedResidents ?? []} {...dragProps} />;
      case 'UnitRoomBedChanges':
        return <UnitRoomBedChangesCard key={config.cardId} data={data?.unitRoomBedChanges ?? []} {...dragProps} />;
      case 'AllergyChanges':
        return <AllergyChangesCard key={config.cardId} data={data?.allergyChanges ?? []} {...dragProps} />;
      case 'MealPatternNotes':
        return (
          <MealPatternNotesCard
            key={config.cardId}
            data={data?.mealPatternNotes ?? []}
            allData={allMPNotesQuery.data}
            onShowAllChange={setShowAllMPNotes}
            {...dragProps}
          />
        );
      case 'SelectiveMenuAuto':
        return <SelectiveMenuAutoCard key={config.cardId} data={data?.selectiveMenuChanges ?? []} {...dragProps} />;
      case 'CensusSummary':
        return <CensusSummaryCard key={config.cardId} data={censusQuery.data ?? { totalResidents: 0, totalGuests: 0, dietTypeBreakdown: [], unitBreakdown: [] }} {...dragProps} />;
      case 'WeightAlerts':
        return <WeightAlertsCard key={config.cardId} data={weightAlertsQuery.data ?? []} {...dragProps} />;
      case 'UpcomingBirthdays':
        return <UpcomingBirthdaysCard key={config.cardId} data={birthdaysQuery.data ?? []} {...dragProps} />;
      case 'BedsideDevices':
        return <BedsideDevicesCard key={config.cardId} data={bedsideHealth} {...dragProps} />;
      default:
        return null;
    }
  };

  // Time span choices. A value saved before the select replaced the 6-hour
  // stepper (36, 90, ...) is kept as an extra choice so it still shows.
  const hoursOptions: { value: number; label: string }[] = [
    ...TIME_SPAN_OPTIONS,
    ...(TIME_SPAN_OPTIONS.some((o) => o.value === selectedHours) ? [] : [{ value: selectedHours, label: `${selectedHours}h` }]),
  ].sort((a, b) => a.value - b.value);

  // The page's own controls: on the breadcrumb row, or on a phone a row of their own (below).
  const pageControls = (
    <>
      {lastUpdated && (
        <Tooltip title={`Last updated ${lastUpdated}`}>
          <span className="dash-tt-updated">
            Updated {lastUpdatedTime}
          </span>
        </Tooltip>
      )}
      {/* describeChild: the field is named "Time span" itself; the tooltip only describes it. */}
      <Tooltip title="Time span" describeChild>
        <span className="dash-tt-hours" data-tour="dashboard-hours">
          <SelectField
            ariaLabel="Time span"
            dataSource={hoursOptions}
            displayExpr="label"
            valueExpr="value"
            value={selectedHours}
            onValueChanged={(v) => handleHoursChange(Number(v))}
            searchEnabled={false}
            showClearButton={false}
            width={90}
          />
        </span>
      </Tooltip>
      <Tooltip title="Refresh all dashboard data">
        <span>
          <IconButton
            size="small"
            className="dash-tt-btn"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh all dashboard data"
          >
            <i className={`bi bi-arrow-clockwise${isRefreshing ? ' is-spinning' : ''}`} aria-hidden="true" />
          </IconButton>
        </span>
      </Tooltip>
      {!isEditMode && (
        <Tooltip title="Customize layout">
          <IconButton
            size="small"
            className="dash-tt-btn"
            onClick={enterEditMode}
            aria-label="Customize layout"
            data-tour="dashboard-customize"
          >
            <i className="bi bi-layout-wtf" aria-hidden="true" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <div className={`dashboard-container page-bare ${isEditMode ? 'dashboard-edit-mode' : ''}`}>
      {/* Last updated, Time span, Refresh and Customize sit on the breadcrumb
          row (Jeff, 2026-10-22: the top was too crowded). The page had a
          header row of its own for them, the third band above the cards.
          On a phone upright the bar has no room for them: two 44px buttons
          left the page name 0px wide ("[", the edge of the D), and the time
          span had to be hidden, so it could not be changed at all (Android
          review HR-1). There they are a short row above the header panel. */}
      {isMobile ? (
        <div className="dashboard-phone-controls">{pageControls}</div>
      ) : (
        <ToolbarPageActions>{pageControls}</ToolbarPageActions>
      )}

      {/* The header panel above the cards: the stat tiles (where the
          facility is right now), then the quick actions on their own row
          (what you came to do). Jeff, 2026-10-22: "bring the old look back"
          after the one-line band; the page controls stay on the breadcrumb
          row. */}
      <div className="dashboard-head">
        {/* --- Layout Controller, edit mode only --- */}
        {isEditMode && (
          <div className="dashboard-layout-toolbar edit-mode-active">
            <div className="layout-toolbar-left">
              <span className="edit-mode-indicator">
                Edit Mode - Drag cards to rearrange
              </span>
            </div>
            {/* Jeff's rule (docs/ui/dialog-actions.md): Reset, Cancel, then Save Layout, the only filled button, at the far right. */}
            <div className="layout-toolbar-right">
              <Button
                size="small"
                variant="outlined"
                startIcon={<i className="bi bi-arrow-counterclockwise" />}
                onClick={resetLayout}
                sx={SECONDARY_BUTTON_SX}
                style={{ fontSize: '0.8rem' }}
              >
                Reset to Default
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<i className="bi bi-x-lg" />}
                onClick={cancelEdit}
                sx={SECONDARY_BUTTON_SX}
                style={{ fontSize: '0.8rem' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<i className="bi bi-check-lg" />}
                onClick={saveLayout}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                Save Layout
              </Button>
            </div>
          </div>
        )}

        {/* --- Card Visibility (Edit Mode) --- */}
        {isEditMode && (
          <div className="card-visibility-panel">
            <span className="visibility-label">Show/Hide Cards:</span>
            <div className="visibility-checkboxes">
              {/* Same retired-card list the grid uses. These were two separate
                  literal filters and had already drifted: the grid excluded
                  MealServiceStatus and this panel did not, so edit mode offered
                  a checkbox for a card that could never appear. */}
              {[...cards]
                .filter((c) => !RETIRED_CARD_IDS.has(c.cardId))
                .sort((a, b) => a.order - b.order).map((card) => (
                <FormControlLabel
                  key={card.cardId}
                  control={
                    <Checkbox
                      size="small"
                      checked={card.isVisible}
                      onChange={(e) => toggleCardVisibility(card.cardId, e.target.checked)}
                    />
                  }
                  label={
                    <span style={{ fontSize: '0.8rem' }}>{localizeResident(card.title)}</span>
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Where the facility is right now. Never collapses, because it is
            never empty. */}
        <NowStrip meal={mealStatusQuery.data} census={censusQuery.data} />

        {/* What you came to do. */}
        <QuickActions savedIds={actionIds} onChange={saveActions} onCommand={runCommand} />
      </div>

      {/* Problems with no card of their own: EHR messages that did not land,
          PDPM documentation gaps. Both were reaching the notification bell and
          nowhere else. Reads the same alerts store the bell does, so the two
          cannot disagree. */}
      <AttentionRows />

      {/* Getting Started progress, only while the wizard is on here and not signed off. */}
      <SetupChecklistCard />

      {/* Held EHR diet changes waiting for review, only where the facility
          holds them (EHR on and diet order locking on). It used to be shown
          everywhere, saying why when it did not apply; Jeff decided on
          2026-10-22 that a card explaining a feature is off is clutter. Where
          it does show, a user without the diet change right still sees the
          reason in the card, and PSI staff get the rule in its info tooltip. */}
      {heldApplies && (
        <HeldEhrChangesCard
          facilityId={facilityId}
          ehrTitle={ehrTitle}
          residentLabel={residentSingular}
          unavailableReason={heldUnavailableReason}
          rows={heldQuery.data}
          isLoading={heldEnabled && heldQuery.isLoading}
          isError={heldEnabled && heldQuery.isError}
          onRetry={() => { void heldQuery.refetch(); }}
          psiNote={isPsi ? `Shown because ${ehrTitle} and diet order locking are on for this facility.` : undefined}
        />
      )}

      {/* --- Content ---
          Two-layer structure so column-count masonry works without breaking
          vertical scrolling: `.dashboard-grid` (outer) owns the scroll,
          `.dashboard-grid-cols` (inner) owns the columns. If column-count
          lived on the outer div, the columns algorithm would see the fixed
          height and spill into a third column horizontally. */}
      {/* Every card the layout shows stays in its place. A card with nothing
          in it shows its own empty message rather than collapsing: cards that
          came and went with the data left people unable to find them. */}
      <div className="dashboard-grid" data-tour="dashboard-grid">
        <div className="dashboard-grid-cols">
          {isLoading
            ? visibleCards.map((config) => (
                <SkeletonCard key={config.cardId} icon={config.icon} title={config.title} />
              ))
            : visibleCards.map((config, idx) => (
                <ResizableCardWrapper
                  key={config.cardId}
                  cardId={config.cardId}
                  height={config.height}
                  canResize={isEditMode}
                  onHeightChange={setCardHeight}
                >
                  {renderCard(config, idx)}
                </ResizableCardWrapper>
              ))
          }
        </div>
      </div>

      {/* Mounted only once opened. The dialog is ~2,600 lines and pulls in
          the report-auto-opener context, so rendering it unconditionally
          made the whole dashboard require a provider it never used — and
          paid the mount cost on every dashboard load for a dialog most
          visits never open. */}
      {visualizerOpen && (
        <TrayTicketVisualizerDialog
          open
          onClose={() => setVisualizerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Wraps a rendered dashboard card in a positioning container that
 * (a) applies a user-set pixel height when saved on the layout,
 * (b) renders a drag-handle at the bottom in edit mode, and
 * (c) commits the new height back to the parent's `localCards`
 *     state (via `onHeightChange`) live during the drag. Height
 *     persists with the rest of the layout when the user clicks
 *     Save Layout.
 *
 * The wrapper is what actually sits in the grid — its inline
 * `height` and `data-h` attribute drive the CSS rules that
 * stretch the inner `.dashboard-card` + `.dashboard-card-wrapper`
 * to fill. Doing this at the wrapper level avoids threading a
 * `height` prop through 15 individual card components.
 */
function ResizableCardWrapper({
  cardId,
  height,
  canResize,
  onHeightChange,
  children,
}: {
  cardId: string;
  height?: number;
  canResize: boolean;
  onHeightChange: (cardId: string, height: number | null) => void;
  children: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startY: number; startHeight: number } | null>(null);

  // The handle reports itself as a slider, and a slider has to declare its
  // range, so both the mouse and keyboard paths clamp to the same bounds
  // rather than the mouse being unbounded and the announced max being fiction.
  // 160 was already the floor; 1200 is well past any useful card height on a
  // real display, so the cap is not reachable in normal use.
  const MIN_H = 160;
  const MAX_H = 1200;

  const onMouseMove = useCallback((e: MouseEvent) => {
    const st = dragStateRef.current;
    if (!st) return;
    const dy = e.clientY - st.startY;
    // 160px floor prevents "collapse to header"
    const next = Math.min(MAX_H, Math.max(MIN_H, st.startHeight + dy));
    onHeightChange(cardId, next);
  }, [cardId, onHeightChange]);

  const onMouseUp = useCallback(() => {
    dragStateRef.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [onMouseMove]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();  // don't start a drag-and-drop reorder
    const container = wrapRef.current;
    if (!container) return;
    dragStateRef.current = { startY: e.clientY, startHeight: container.offsetHeight };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [onMouseMove, onMouseUp]);

  /**
   * Keyboard equivalent of the drag.
   *
   * Resizing was mouse-only — the handle had an aria-label but no way to
   * operate it, which is arguably worse than being unlabelled: it announces a
   * control that cannot be used. Arrow keys nudge, Home restores the card to
   * its automatic height (what "never resized" looks like).
   *
   * The 160px floor matches onMouseMove so both routes agree.
   */
  const onResizeKeyDown = useCallback((e: React.KeyboardEvent) => {
    const STEP = 24;
    const current = wrapRef.current?.offsetHeight ?? 0;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.key === 'ArrowDown' ? STEP : -STEP;
      onHeightChange(cardId, Math.min(MAX_H, Math.max(MIN_H, current + delta)));
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      e.stopPropagation();
      onHeightChange(cardId, null);
    }
  }, [cardId, onHeightChange]);

  return (
    <div
      ref={wrapRef}
      className="dashboard-card-outer"
      style={{
        position: 'relative',
        // When a height is set, the outer becomes a fixed-height
        // grid item and the inner `.dashboard-card` stretches via
        // the CSS rule `.dashboard-card-outer[data-h] > .dashboard-card`.
        height: height ? `${height}px` : undefined,
      }}
      data-h={height ? '1' : undefined}
    >
      {children}
      {canResize && (
        <div
          className="card-resize-handle"
          // Reported as a slider rather than a separator. Both describe a
          // resize grip, but ARIA's `separator` is only interactive when
          // focusable and tooling generally treats it as static — jsx-a11y
          // flags a tabIndex on it. `slider` is interactive by definition,
          // carries the current height in its announcement, and is the
          // closer match for "arrow keys change a value".
          role="slider"
          aria-orientation="vertical"
          aria-valuemin={MIN_H}
          aria-valuemax={MAX_H}
          aria-valuenow={height ?? wrapRef.current?.offsetHeight ?? MIN_H}
          aria-valuetext={height ? `${height} pixels` : 'automatic height'}
          tabIndex={0}
          onMouseDown={onMouseDown}
          onKeyDown={onResizeKeyDown}
          title="Drag to resize, or focus and use arrow keys"
          aria-label={`Resize ${cardId} card. Arrow keys resize, Home restores automatic height.`}
        >
          ⋯
        </div>
      )}
    </div>
  );
}
